/**
 * Room Layout API Hooks
 * 
 * React Query hooks for room layout operations
 * Date: 09-10-2025 12:40:09
 * Updated: 09-10-2025 13:53:00 - Fixed token management to use TokenManager
 * 
 * @module hooks/api/useRoomLayout
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { API_CONFIG } from '../../api';
import { TokenManager } from '../../api/interceptors';
import type {
  RoomLayout,
  LayoutData,
  BatchLayoutUpdate,
  LayoutValidationRequest,
  LayoutValidationResponse
} from '../../types/room-layout';

const API_BASE = API_CONFIG.BASE_URL;

/**
 * Fetch all room layouts for a floor
 * 
 * @param floorId - Floor ID to fetch layouts for
 * @returns React Query result with room layouts
 */
export function useRoomLayouts(floorId: string | null) {
  return useQuery({
    queryKey: ['room-layouts', floorId],
    queryFn: async () => {
      if (!floorId) return { layouts: [] };

      const token = TokenManager.getAccessToken();
      const response = await fetch(`${API_BASE}/room-layout/${floorId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to fetch room layouts');
      }

      const data = await response.json();
      return data.data;
    },
    enabled: !!floorId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });
}

/**
 * Update room layout (single or batch)
 * 
 * @returns React Query mutation for updating room layouts
 */
export function useUpdateRoomLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LayoutData | BatchLayoutUpdate) => {
      const token = TokenManager.getAccessToken();
      const response = await fetch(`${API_BASE}/room-layout`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to update room layout');
      }

      const result = await response.json();
      return result.data;
    },
    onSuccess: async (data, variables) => {
      // Invalidate floor plan data to trigger refetch
      const floorId = 'floorId' in variables ? variables.floorId : (variables as LayoutData).floorId;
      
      // Invalidate all related queries with proper keys
      await Promise.all([
        // Room layouts specific to floor
        queryClient.invalidateQueries({ queryKey: ['room-layouts', floorId] }),
        // ALL floorPlan queries (matches useFloorPlanData key)
        queryClient.invalidateQueries({ 
          queryKey: ['floorPlan'],
          refetchType: 'active' // Force refetch currently active queries
        }),
        // Refetch immediately to update UI
        queryClient.refetchQueries({ 
          queryKey: ['floorPlan'], 
          type: 'active'
        })
      ]);
      
      notifications.show({
        title: 'Layout Saved',
        message: 'Room layout updated successfully',
        color: 'green'
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Save Failed',
        message: error.message,
        color: 'red'
      });
    }
  });
}

/**
 * Delete a room layout
 * 
 * @returns React Query mutation for deleting room layouts
 */
export function useDeleteRoomLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roomId: string) => {
      const token = TokenManager.getAccessToken();
      const response = await fetch(`${API_BASE}/room-layout/${roomId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to delete room layout');
      }

      const result = await response.json();
      return result.data;
    },
    onSuccess: async () => {
      // Invalidate all floor plan data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['room-layouts'] }),
        queryClient.invalidateQueries({ 
          queryKey: ['floorPlan'],
          refetchType: 'active'
        }),
        // Force immediate refetch
        queryClient.refetchQueries({ 
          queryKey: ['floorPlan'], 
          type: 'active'
        })
      ]);
      
      notifications.show({
        title: 'Layout Removed',
        message: 'Room layout deleted successfully',
        color: 'blue'
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Delete Failed',
        message: error.message,
        color: 'red'
      });
    }
  });
}

/**
 * Validate room layout for overlaps
 * 
 * @returns React Query mutation for validating layouts
 */
export function useValidateRoomLayout() {
  return useMutation({
    mutationFn: async (request: LayoutValidationRequest): Promise<LayoutValidationResponse> => {
      const token = TokenManager.getAccessToken();
      const response = await fetch(`${API_BASE}/room-layout/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to validate layout');
      }

      const result = await response.json();
      return result.data;
    }
  });
}

/**
 * Create room layout (for newly positioned room)
 * Uses PATCH endpoint which handles upsert
 * 
 * @returns React Query mutation for creating room layout
 * @created 15-10-2025 23:45:00
 */
export function useCreateRoomLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { roomId: string; floorId: string; x: number; y: number; width: number; height: number }) => {
      const token = TokenManager.getAccessToken();
      const response = await fetch(`${API_BASE}/room-layout`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to create room layout');
      }

      const result = await response.json();
      return result.data;
    },
    onSuccess: async (data, variables) => {
      // Invalidate all related queries to trigger refetch
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['room-layouts', variables.floorId] }),
        queryClient.invalidateQueries({ 
          queryKey: ['floorPlan'],
          refetchType: 'active'
        }),
        queryClient.invalidateQueries({ queryKey: ['rooms', 'unpositioned', variables.floorId] }),
        // Force immediate refetch for active floor plan queries
        queryClient.refetchQueries({ 
          queryKey: ['floorPlan'], 
          type: 'active'
        })
      ]);
      
      notifications.show({
        title: 'Room Positioned',
        message: 'Room positioned on floor plan successfully',
        color: 'green'
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Positioning Failed',
        message: error.message,
        color: 'red'
      });
    }
  });
}

